import { useState, useEffect, useRef } from 'react';

export default function BioStats() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 4.76, label: 'ERA', suffix: '' },
    { value: 124, label: 'Strikeouts', suffix: '' },
    { value: 109, label: 'Innings Pitched', suffix: '' },
    { value: 1.5, label: 'WHIP', suffix: '' },
    { value: 21, label: 'Saves', suffix: '' },
  ];

  const AnimatedNumber = ({ value, suffix }: { value: number; suffix: string }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(current);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }, [isVisible, value]);

    return (
      <span>
        {value % 1 === 0 ? Math.round(count) : count.toFixed(2)}
        {suffix}
      </span>
    );
  };

  return (
    <section id="bio" ref={sectionRef} className="bg-[#0d0d0d] py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Bio Column */}
          <div className="lg:col-span-5">
            <div className="mb-6">
              <span className="text-[#8B0000] text-sm font-bold tracking-widest uppercase">
                About Nightmare
              </span>
              <div className="w-16 h-0.5 bg-[#8B0000] mt-2"></div>
            </div>

            <h2 className="text-5xl font-bold text-white mb-8 font-serif">
              The Story Behind the Name
            </h2>

            <div className="space-y-6 text-gray-400 text-lg leading-relaxed">
              <p>
                Zane Morehouse earned his nickname "Nightmare" during his first year at the University of Texas. Hall Of Famer, Troy Tulowitzki (Texas's Hitting Coach) was doing scouting reports on the team's pitchers with the hitters. Troy said he was a nightmare for hitters and later sent him the song "Nightmare" by Avenged Sevenfold to use as his walkout song. What started as a motivational gesture quickly became a badge of honor that perfectly encapsulates his intimidating presence on the mound and the dread opposing batters feel when facing his devastating cutter-slider-splitter-sinker arsenal.
              </p>

              <blockquote className="text-2xl italic text-[#8B0000] font-serif my-8 pl-6 border-l-4 border-[#8B0000]">
                "Every pitch is a statement. Every strikeout is a reminder."
              </blockquote>

              <p>
                Born in Waco, Texas, Zane is the son of Allen and Sandra Morehouse. Allen, an ex-PBR/PRCA bull rider, and Sandra, a VP of finance, instilled in him the values of grit and determination. Zane played his youth years for the Tris Speaker Youth Club in Hubbard, Texas—named after the Hubbard native and former Cleveland Indian, one of the best center fielders to ever play the game. His high school career at Dawson High School in Texas saw him become a district standout and earn All-Golden Circle honors. A Perfect Game All-American with a 4.0 GPA, Zane was also an impact player in basketball, showcasing his exceptional athleticism across multiple sports.
              </p>

              <p>
                Baseball excellence runs deep in the Morehouse bloodline. Zane's fourth great uncle is Charlie Robertson, who etched his name into baseball immortality on April 30, 1922, when he threw a perfect game for the Chicago White Sox against the Detroit Tigers at Navin Field. Robertson's perfect game was only the fifth in Major League Baseball history and the first in the American League. Despite facing a lineup that included Hall of Famer Ty Cobb, Robertson retired all 27 batters he faced in a dominant 2-0 victory. His perfect game remained the only one thrown by a White Sox pitcher until Philip Humber accomplished the feat 90 years later in 2012. This legendary connection to one of baseball's rarest achievements serves as both inspiration and a reminder that greatness is woven into Zane's DNA.
              </p>

              <p>
                Zane's journey to the Cleveland Guardians organization has been nothing short of remarkable. His relentless work ethic and competitive fire have propelled him through the minor leagues, earning recognition as one of the organization's top pitching prospects.
              </p>

              <p>
                Off the field, Zane is passionate about giving back to the community and inspiring the next generation of baseball players. His "Nightmare Collection" merchandise line reflects his bold personality and connects fans with his journey from small town country kid to professional athlete.
              </p>
            </div>

            <div className="mt-10">
              <p className="text-4xl text-gray-300 font-script italic">Zane Morehouse</p>
            </div>
          </div>

          {/* Stats Column */}
          <div id="stats" className="lg:col-span-7">
            <div
              className="bg-[#141414] rounded-lg p-10 border border-gray-800 shadow-2xl shadow-black/50"
              style={{
                backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 0, 0, 0.03) 0%, transparent 50%)',
              }}
            >
              <div className="grid grid-cols-2 gap-8">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="text-center pb-8 border-b border-gray-800"
                  >
                    <div className="text-6xl font-bold text-white mb-3">
                      <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-sm uppercase tracking-widest text-gray-500 font-semibold">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Baseball Diamond Pattern */}
              <div className="mt-10 pt-10 border-t border-gray-800">
                <div className="flex items-center justify-center space-x-8 text-gray-600">
                  <div className="text-center">
                    <i className="ri-trophy-line text-4xl text-[#8B0000] mb-2"></i>
                    <p className="text-xs uppercase tracking-wider">2024 Season</p>
                  </div>
                  <div className="text-center">
                    <i className="ri-star-line text-4xl text-[#8B0000] mb-2"></i>
                    <p className="text-xs uppercase tracking-wider">Top Prospect</p>
                  </div>
                  <div className="text-center">
                    <i className="ri-baseball-line text-4xl text-[#8B0000] mb-2"></i>
                    <p className="text-xs uppercase tracking-wider">Pro Pitcher</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                      <img 
                        src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/5e80cf65c18db2f0195616ec617964b3.png" 
                        alt="Cleveland Guardians Logo" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <p className="text-xs uppercase tracking-wider">Cleveland Guardians</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Section */}
            <div className="mt-40">
              <div className="mb-6">
                <span className="text-[#8B0000] text-sm font-bold tracking-widest uppercase">
                  Watch Nightmare
                </span>
                <div className="w-16 h-0.5 bg-[#8B0000] mt-2"></div>
              </div>
              
              <div className="bg-[#141414] rounded-lg border border-gray-800 overflow-hidden shadow-2xl shadow-black/50">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/VIDEO_ID_HERE"
                    title="Zane Morehouse Highlights"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Nightmare on the Mound</h3>
                  <p className="text-gray-500 text-sm">Watch Zane Morehouse dominate with his devastating pitch arsenal. From strikeouts to clutch performances, see why opposing batters dread facing the Nightmare.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
